//Global
import React, { useState, useEffect } from 'react';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

//lib
import { useDictionary } from 'lib/hooks/use-dictionary';

//helper
import SVG from 'helpers/SVG/SVG';
import Button from 'helpers/Button/Button';

interface SharebarProps {
  params: Params;
  fields: SharebarFieldsProps;
}
interface SharebarFieldsProps {
  children: SharebarItemProps[];
}
interface SharebarItemProps {
  id: string;
  displayName: string;
  name: string;
  templateId: string;
  templateName: string;
  url: string;
  fields: Fields;
}
interface Fields {
  ShareLinkMarkup: Field<string>;
}

interface Params {
  SharebarNetworks: string;
  ShowSubscribeButton: string;
}

export type PageTitleProps = {
  MetaOGDescription: Field<string>;
  Headline: Field<string>;
  PageTitle: Field<string>;
  Teaser: Field<string>;
};

const Sharebar = (props: SharebarProps): JSX.Element => {
  const { children } = props?.fields;
  const [svgs, setSvgs] = useState<string[]>([]);
  const [displayIcon, setDisplayIcon] = useState<boolean>(false);
  const { sitecoreContext } = useSitecoreContext();
  const { PageTitle, Teaser } = sitecoreContext?.route?.fields as PageTitleProps;

  const dict = useDictionary();
  const subscribeBtnText = dict?.['ArticleSubscribeButton'] || 'Subscribe';

  const [isFixedDivVisible, setIsFixedDivVisible] = useState(true);

  const isScrolledIntoView = (elem: HTMLElement | null): boolean => {
    if (!elem) {
      return false;
    }

    const docViewTop = window.scrollY;
    const docViewBottom = docViewTop + window.innerHeight;

    const elemTop = elem.getBoundingClientRect().top + docViewTop;

    return elemTop < docViewBottom;
  };

  const removeHTMLTags = (html: string) => {
    const refineHtmlString = html && html.replace(/<[^>]*>/g, '');
    return refineHtmlString;
  };

  useEffect(() => {
    const handleScroll = () => {
      const footerElement = (document.querySelector('.footer') as HTMLElement) || undefined;
      if (isScrolledIntoView(footerElement)) {
        setIsFixedDivVisible(false);
      } else {
        setIsFixedDivVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup function to remove the event listener when the component is unmounted
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const arr: string[] = [];
    children.forEach((fields) => {
      let actualString = fields?.fields?.ShareLinkMarkup?.value;
      const pageTitleWithoutHtml = removeHTMLTags(PageTitle?.value);
      const TeaserWithoutHtml = removeHTMLTags(Teaser?.value);

      actualString = actualString.replace('{Title}', pageTitleWithoutHtml);
      actualString = actualString.replace('{BodyTeaser}', TeaserWithoutHtml);
      actualString = actualString.replace('{ShareUrl}', decodeURI(window?.location?.href));
      arr.push(actualString);
    });
    setSvgs(arr);
  }, [children]);

  const openMobileSharebar = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    window &&
      window?.navigator?.share &&
      window?.navigator?.share({
        title: PageTitle.value,
        url: decodeURI(window && window?.location?.href),
      });
  };

  return (
    <>
      <section className="lg:w-[190px]">
        {/* Subscribe */}
        {props?.params?.ShowSubscribeButton && (
          <div className="hidden lg:block jsSubscribe">
            <Button type="auxiliary" label={subscribeBtnText} />
          </div>
        )}

        {/* END Subscribe */}
        <div
          onMouseLeave={() => {
            setDisplayIcon(false);
          }}
          className="flex items-center mt-4 justify-between bottom-10"
        >
          <div className="lg:block hidden flex-col">
            <div className="flex flex-col gap-y-4 items-center">
              <div
                tabIndex={0}
                className="flex items-center justify-center p-1 cursor-pointer focus:outline focus:outline-light-stroke-primary-variant focus:rounded"
                onMouseEnter={() => {
                  setDisplayIcon(true);
                }}
                onFocus={() => {
                  setDisplayIcon(true);
                }}
              >
                <SVG
                  className="[&_path]:fill-light-fill-secondary [&_svg]:h-6 [&_svg]:w-6"
                  svg="icon-share2"
                />
              </div>
            </div>
          </div>

          {svgs &&
            svgs.map((item, index) => (
              <div
                key={index}
                className={`${
                  displayIcon ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'
                } hidden lg:flex bg-none h-8 w-8 [&_a]:p-1 focus:[&_a]:outline focus:[&_a]:rounded focus:[&_a]:outline-light-stroke-primary-variant [&_svg]:h-6 [&_svg]:w-6 [&_path]:fill-light-fill-primary-variant hover:[&_path]:fill-light-fill-secondary`}
                dangerouslySetInnerHTML={{ __html: item }}
              ></div>
            ))}
        </div>
      </section>
      {/* Mobile / Tablet */}
      <div
        className={`${
          isFixedDivVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } z-20 bottom-10 lg:hidden fixed right-[39px] bg-light-background-neutral-variant inline-flex gap-x-[9px] text-light-fill-secondary px-4 py-2 items-center border-[1px] border-color-dark-2 h-10 rounded-[20px]`}
      >
        {props?.params?.ShowSubscribeButton && (
          <button className="jsSubscribe outline-none p2-medium link">{subscribeBtnText}</button>
        )}
        <div className="border-l-[1px] h-[20px] border border-color-dark-2"></div>
        <button
          onClick={() => {
            openMobileSharebar();
          }}
          title="Share"
          className="outline-none h-6 w-6 cursor-pointer flex"
        >
          <SVG className="[&_path]:fill-light-fill-secondary" svg="icon-share2" />
        </button>
      </div>
      {/* END */}
    </>
  );
};

export default Sharebar;
